import { Component, Inject, Input, OnInit, ViewChild } from "@angular/core";
import { select, Store } from "@ngrx/store";
import { selectRouter } from "@shared/store/router/router.selector";
import { IEnvironmentConfig } from "@webpack/extra-webpack-types";
import { Observable } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap } from "rxjs/operators";
import { PracticeService } from "src/app/core/http/practice.service";
import { SidebarComponent } from "@syncfusion/ej2-angular-navigations";
import {
  BackgroundClasses,
  IHeaderVisibilitySettings,
} from "../../models/interfaces";

interface Practice {
  href: string;
  name: string;
}

/**
 * The master layout component which is used within the app component to wrap the header, footer, and main sections for the entire app.
 *
 * **Warning:** This component is incomplete. It does not deal with valueChanges and the content is projected along side
 * the default header, body, and footer. Also, this doesn't really work with routing--because in that case the intent is
 * to use this in the router, there's no way to set the header params, so you have to either wrap it in a component or
 * maybe have a service.
 *
 * Usage: Adding content with the classes `header-content`, `body-contnent`, and `footer-content` will project them in
 * to the appropriate part of the template. Additionally, Foundation-Header inputs/properties can be set on this component.
 */
@Component({
    selector: "ofi-foundation-layout",
    templateUrl: "./layout.component.html",
    styleUrls: ["./layout.component.scss"],
    standalone: false
})
export class FoundationLayoutComponent implements OnInit {
  /** The name of the classes that will be applied to the background. No value will  */
  // eslint-disable-next-line  @typescript-eslint/no-redundant-type-constituents
  @Input() backgroundClassList?: (string | BackgroundClasses)[];
  /** A template to use inside the sidebar. */
  // eslint-disable-next-line  @typescript-eslint/no-unsafe-function-type
  @Input() menuButtonCB: Function;

  public practice: Observable<Practice>;
  /** We're going to need to know practice information eventually. :x */
  @ViewChild("sidebar", { static: true })
  public sideBar: SidebarComponent;
  /** The name that will appear next to the user icon. **The user's first name is recommended**. */
  @Input() username: string;
  /** Which items should appear in the header. */
  @Input() visibility: IHeaderVisibilitySettings;

  constructor(
    @Inject("ENV") private _env: IEnvironmentConfig,
    private _store: Store,
    private _service: PracticeService
  ) {}

  public back(): void {
    window.history.back();
  }

  public hamburgerClick(): void {
    this.sideBar.show();
  }

  ngOnInit(): void {
    this.practice = this._store.pipe(
      select(selectRouter),
      map((r) => +r.state.params.practiceId),
      filter((t) => !!t),
      distinctUntilChanged(),
      switchMap((practiceId) =>
        this._service.get(practiceId).pipe(
          map((p) => ({
            href: `${this._env.portalUrl}/Practice/Detail/${p.id}`,
            name: p.name,
          }))
        )
      )
    );
    this.backgroundClassList = this.backgroundClassList
      ? ["layout", ...this.backgroundClassList]
      : ["layout", "responsive", "fill-white", "bg-tools", "background-tools"];
  }
}
